// src/lib/api/billing.ts — billing / subscription (PRI-291 Phase 7)
// checkout, portal, usage, api keys, pricing config, discount codes

// PRI-496 P0: billing portal endpoints are internal — route through the
// internal-api-client chokepoint (internalRequest === legacy request()).
// PRI-496 slice 1: endpoints that ARE in the public contract (/v1/usage,
// /v1/credit-pricing) ride the public SDK transport directly — same request()
// semantics, but no longer counted as internal-chokepoint traffic.
import { internalRequest as request } from '../../core/lib/internal-api-client'
import { request as publicRequest } from '../../core/lib/api/client'
import { sdk } from '../../core/lib/api/sdk'
import { trackDiscountCodeRedeemed } from '../analytics/analytics'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UsageData {
  minutesUsed: number
  minutesTotal: number
  periodStart: string
  periodEnd: string
  dailyBreakdown: Array<{ date: string; minutes: number }>
}

interface RawUsage {
  period: { start: string; end: string }
  plan: string
  has_active_subscription: boolean
  video_minutes: number
  video_minutes_limit: number
  max_video_length_minutes: number
  api_calls: number
  reset_at: string
}

export interface UsageResponse {
  queries_used: number
  queries_total: number
  minutes_used: number
  minutes_total: number
  period_end: string
  max_video_minutes: number
  plan: 'free' | 'pro' | 'enterprise'
  has_active_subscription: boolean
}

export interface ApiKey {
  id: string
  name: string
  key: string
  maskedKey: string
  createdAt: string
  lastUsed: string | null
}

/** PRI-437 P8: admin credit price config row (credits era). */
export interface AdminCreditPriceConfig {
  price_per_second_cents: number;
  /** PRI-552: price per processed frame, in cents. */
  price_per_frame_cents?: number;
  /** PRI-552: free signup grant as a frame budget (108,000 = 1h @ 30fps). */
  signup_grant_frames?: number;
  signup_grant_seconds: number;
  card_grant_seconds: number;
  signup_grant_expires_after_days: number;
  auto_refill_threshold_seconds: number;
  min_purchase_cents: number;
  allowed_purchase_cents: number[];
  custom_purchase_enabled: boolean;
  enterprise_monthly_commitment_threshold_cents?: number;
  updated_at?: string;
}

export interface DiscountCode {
  id: string;
  code: string;
  discount_type: 'percent_off' | 'free_access';
  percent_off: number | null;
  free_tier: string | null;
  duration_days: number | null;
  max_uses: number | null;
  uses_count: number;
  active: boolean;
  created_at: string;
}

// ── Functions ─────────────────────────────────────────────────────────────────

export function getBillingPortalUrl(): Promise<{ portal_url: string }> {
  return request<{ portal_url: string }>('/v1/billing/portal')
}

export function getUsage(): Promise<UsageResponse> {
  // PRI-496: GET /v1/usage is in the public contract — public transport.
  // Still reads the legacy flat fields (deprecated but kept indefinitely per
  // PRI-477-F); meters[] adoption is a Dashboard revamp, not a transport move.
  return publicRequest<RawUsage>('/v1/usage').then(r => ({
    queries_used: r.api_calls,
    queries_total: 10000,
    minutes_used: r.video_minutes,
    minutes_total: r.video_minutes_limit,
    period_end: r.reset_at,
    max_video_minutes: r.max_video_length_minutes,
    plan: (r.plan === 'pro' ? 'pro' : r.plan === 'enterprise' ? 'enterprise' : 'free') as UsageResponse['plan'],
    has_active_subscription: r.has_active_subscription ?? false,
  }))
}

export function getApiKeys(): Promise<ApiKey[]> {
  return request<{ keys: ApiKey[] }>('/v1/api-keys').then(r => r.keys)
}

export const listApiKeys = getApiKeys

export function createApiKey(name: string): Promise<ApiKey> {
  return request<ApiKey>('/v1/api-keys', {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

export function revokeApiKey(id: string): Promise<void> {
  return request<void>(`/v1/api-keys/${id}`, { method: 'DELETE' })
}

export function rotateApiKey(id: string): Promise<ApiKey & { rotated_from: string }> {
  return request<ApiKey & { rotated_from: string }>(`/v1/api-keys/${id}/rotate`, {
    method: 'POST',
  })
}

export function redeemCode(code: string): Promise<{
  ok: boolean;
  type: 'free_access' | 'percent_off';
  plan_tier?: string;
  expires_at?: string | null;
  percent_off?: number;
  stripe_promo_code?: string;
}> {
  return request<{
    ok: boolean;
    type: 'free_access' | 'percent_off';
    plan_tier?: string;
    expires_at?: string | null;
    percent_off?: number;
    stripe_promo_code?: string;
  }>('/v1/redeem-code', { method: 'POST', body: JSON.stringify({ code }) }).then((res) => {
    // Fire-and-forget analytics — no UI currently calls redeemCode, so the
    // client success path is the single choke point (analytics gap scan 2026-07-12).
    if (res.ok) trackDiscountCodeRedeemed(code, res.type, res.plan_tier ?? '')
    return res
  });
}

export function getAdminCreditPriceConfig(): Promise<{ config: AdminCreditPriceConfig }> {
  return request<{ config: AdminCreditPriceConfig }>('/v1/admin/credit-price-config');
}

export function updateAdminCreditPriceConfig(config: Record<string, unknown>): Promise<{ ok: boolean; config: AdminCreditPriceConfig }> {
  return request('/v1/admin/credit-price-config', { method: 'PUT', body: JSON.stringify(config) });
}

export function getAdminDiscountCodes(): Promise<{ codes: DiscountCode[] }> {
  return request<{ codes: DiscountCode[] }>('/v1/admin/discount-codes');
}

// 2026-08-07: free_access creation removed with the legacy subscription tiers
// (API returns 410 for it). Existing rows may still carry discount_type
// 'free_access' for historical display, hence the wider DiscountCode type.
export function createAdminDiscountCode(data: {
  code?: string;
  discount_type: 'percent_off';
  percent_off: number;
  duration_days?: number | null;
  max_uses?: number | null;
}): Promise<{ ok: boolean; code: DiscountCode }> {
  return request('/v1/admin/discount-codes', { method: 'POST', body: JSON.stringify(data) });
}

// ─── PRI-437 P8 — credits-era billing API ─────────────────────────────────────

export interface CreditPricing {
  price_per_second_cents: number
  /** PRI-552: price per processed frame, in cents. Optional — older API omits it. */
  price_per_frame_cents?: number
  /** PRI-552: free signup grant as a frame budget (108,000 = 1h @ 30fps). Optional — older API omits it. */
  signup_grant_frames?: number
  signup_grant_seconds: number
  card_grant_seconds: number
  auto_refill_threshold_seconds: number
  allowed_purchase_cents: number[]
  custom_purchase_enabled: boolean
  min_purchase_cents: number
  /** PRI-508: batch analyses discount (% off each prompt after the first). Optional — older API omits it. */
  batch_discount_pct?: number
  batch_min_prompts?: number
  batch_max_prompts?: number
  /** PRI-554: pricing-policy status — "preview" while preview pricing is in effect. Optional — older API omits it. */
  pricing_tier?: string
  /** PRI-554: plain-language pricing policy (canonical caveat from the API). Optional — older API omits it. */
  pricing_tier_note?: string
}

export interface CreditsSummary {
  balance_seconds: number
  card_grant_awarded: boolean
  payment_method: { brand: string; last4: string } | null
  auto_refill: { enabled: boolean; threshold_seconds: number; amount_cents: number | null }
  notifications: { low_balance: boolean; refill_failed: boolean }
  purchase: {
    allowed_amount_cents: number[]
    custom_enabled: boolean
    min_amount_cents: number
    price_per_second_cents: number
  }
  grants: { signup_grant_seconds: number; card_grant_seconds: number }
}

export interface CreditTransaction {
  id: string
  kind: string
  seconds_delta: number
  balance_after_seconds: number
  credit_class: string | null
  source_type: string | null
  source_id: string | null
  created_at: string
}

export interface UsageSummary {
  window_days: number
  total_seconds_used: number
  by_day: Array<{ date: string; seconds: number }>
  by_source: { upload_seconds: number; stream_seconds: number; other_seconds: number }
  grant_seconds_added: number
  purchased_seconds_added: number
}

/** Public — pricing page reads launch rate + grants from config.
 *  PRI-496: GET /v1/credit-pricing is a public-contract endpoint — served by
 *  the typed SDK resource (sdk.creditPricing), not the internal chokepoint. */
export function getCreditPricing(): Promise<CreditPricing> {
  return sdk.creditPricing.retrieve()
}

export function getCreditsSummary(): Promise<CreditsSummary> {
  return request<CreditsSummary>('/v1/billing/credits')
}

export function getCreditTransactions(limit = 50): Promise<{ transactions: CreditTransaction[] }> {
  return request<{ transactions: CreditTransaction[] }>(`/v1/billing/transactions?limit=${limit}`)
}

export function getUsageSummary(days = 30): Promise<UsageSummary> {
  return request<UsageSummary>(`/v1/billing/usage-summary?days=${days}`)
}

export function startCardSetup(): Promise<{ checkout_url: string }> {
  return request<{ checkout_url: string }>('/v1/billing/setup-payment-method', {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export function purchaseCredits(amountCents: number, useSavedCard = false): Promise<{
  status: 'succeeded' | 'checkout'
  checkout_url?: string
  seconds?: number
  seconds_added?: number
  balance_seconds?: number | null
}> {
  return request('/v1/billing/purchase', {
    method: 'POST',
    body: JSON.stringify({ amount_cents: amountCents, use_saved_card: useSavedCard }),
  })
}

export function configureAutoRefill(enabled: boolean, amountCents?: number): Promise<{ enabled: boolean }> {
  return request('/v1/billing/refill', {
    method: 'PUT',
    body: JSON.stringify({ enabled, ...(amountCents != null ? { amount_cents: amountCents } : {}) }),
  })
}

export function updateBillingNotifications(prefs: {
  notify_low_balance?: boolean
  notify_refill_failed?: boolean
}): Promise<Record<string, boolean>> {
  return request('/v1/billing/notifications', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  })
}
